import { useEffect, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.entry';
import { useTranslation } from '../../../hooks/useTranslation';

type PdfViewerProps = {
    url: string;
    scale?: number;
};

export const PdfViewer = ({ url, scale = 1 }: PdfViewerProps) => {
    const { t } = useTranslation('invoice');
    const [pages, setPages] = useState<string[]>([]);

    useEffect(() => {
        if (!url) {
            setPages([]);
            return;
        }

        let cancelled = false;

        const renderPdf = async () => {
            try {
                const pdf = await pdfjsLib.getDocument(url).promise;
                const numPages = pdf.numPages;

                const renderedPages: string[] = [];

                for (let i = 1; i <= numPages; i++) {
                    const pdfPage = await pdf.getPage(i);
                    const viewport = pdfPage.getViewport({ scale });

                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d', { willReadFrequently: true })!;

                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    await pdfPage.render({ canvasContext: context, viewport }).promise;

                    if (cancelled) return;

                    renderedPages.push(canvas.toDataURL('image/png'));
                }

                if (!cancelled) {
                    setPages(renderedPages);
                }
            } catch (err) {
                console.error('PDF render error:', err);
                if (!cancelled) setPages([]);
            }
        };

        renderPdf();

        return () => {
            cancelled = true;
        };
    }, [url, scale]);

    return (
        <div className="pdf-viewer-wrapper">
            {pages.length > 0 ? (
                pages.map((src, index) => <img key={index} src={src} alt={`PDF page ${index + 1}`} className="pdf-viewer" />)
            ) : (
                <p>{t('notSelected')}</p>
            )}
        </div>
    );
};
